import React from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Typogaphy } from 'vnetwork-ui'
import { ArrowRight, AlertCircle, Diff } from 'lucide-react'
import {
    VersionBumpType,
    formatVersion,
} from '@/modules/Asset/utils/version-bump'

const BUMP_BADGE_STYLES: Record<string, string> = {
    major: 'bg-red-100 text-red-800 border-red-300',
    minor: 'bg-blue-100 text-blue-800 border-blue-300',
    patch: 'bg-gray-100 text-gray-800 border-gray-300',
}

interface ChangedField {
    field: string
    label: string
    oldValue: string
    newValue: string
}

interface ReviewVersionChangesCardProps {
    versionBumpType: VersionBumpType
    currentVersion: { major: number; minor: number; patch: number }
    nextVersion: { major: number; minor: number; patch: number }
    changedFields: ChangedField[]
}

const ReviewVersionChangesCard: React.FC<ReviewVersionChangesCardProps> = ({
    versionBumpType,
    currentVersion,
    nextVersion,
    changedFields,
}) => {
    const { t } = useTranslation('asset')

    return (
        <Card
            className={`shadow-sm overflow-hidden ${
                versionBumpType === 'none'
                    ? 'border-amber-200 bg-amber-50/30'
                    : 'border-blue-200 bg-blue-50/30'
            }`}
        >
            <CardHeader className="border-b border-blue-50 pb-3">
                <CardTitle className="flex items-center gap-2">
                    <Typogaphy className="font-semibold text-primary">
                        {t('form.versionUpdate')}
                    </Typogaphy>
                </CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
                {versionBumpType !== 'none' ? (
                    <div className="flex items-center gap-4">
                        <span className="font-mono text-sm text-gray-600">
                            {formatVersion(currentVersion)}
                        </span>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                        <span className="font-mono text-sm font-bold text-gray-900">
                            {formatVersion(nextVersion)}
                        </span>
                        <Badge
                            variant="outline"
                            className={`text-xs ${BUMP_BADGE_STYLES[versionBumpType] || ''}`}
                        >
                            {t(`form.versionBump_${versionBumpType}`)}
                        </Badge>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 text-amber-700">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm">
                            {t('form.noChangesDetected')}
                        </span>
                    </div>
                )}

                {changedFields.length > 0 && (
                    <div className="border-t border-blue-100 pt-3">
                        <div className="flex items-center gap-2 mb-2">
                            <Diff className="h-4 w-4 text-gray-500" />
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('form.changesSummary')}
                            </span>
                            <Badge variant="outline" className="text-xs">
                                {changedFields.length}
                            </Badge>
                        </div>
                        <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
                            {changedFields.map((change) => (
                                <div
                                    key={change.field}
                                    className="flex items-center gap-4 px-4 py-2.5"
                                >
                                    <span className="text-sm text-gray-500 min-w-36 shrink-0">
                                        {t(change.label)}
                                    </span>
                                    <span className="text-sm text-red-500 line-through truncate max-w-48">
                                        {change.oldValue}
                                    </span>
                                    <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />
                                    <span className="text-sm font-medium text-green-700 truncate max-w-48">
                                        {change.newValue}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export default ReviewVersionChangesCard
